import React, { useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import styled from "styled-components";
import { useDispatch } from "react-redux";

import {
    SelectionBox,
    SelectGroup,
    AccountSelection,
    H1,
    SimplifiedSelectDateRange,
    ContentHolder,
    Loading
} from "commons";
import { convertIdToReal } from "commons/src/NuDateUtils";
import { checkIfScrolledToBottom } from "commons/src/helpers/InfiniteScrollHelpers";
import { IN_PROGRESS, isInProgress, isFailure } from "commons/src/helpers/RequestHelpers";
import { setDocuments, setUnreadDocumentsPresent } from "commons/src/reducers/documentsActions";
import {
    getAccountWrapperTitle,
    getDocumentCategoryOptions,
    filterAccounts,
    filterCategories,
    getDocumentsWithUpdatedCategories
} from "commons/src/helpers/documentHelpers";

import { Message } from "../../components/Message";
import { Document } from "./Document";
import { requestObject } from "../../reducers/requestsActions";
import { ERROR, getRefetchMessageAndAction } from "../../helpers/ErrorHelpers";
import { fetchMoreCorrespondences, retryFetch } from "../../components/DataOperations";
import { routes } from "../../helpers/RouteHelpers";

export function DocumentsList({
    requestStatus,
    nextPage,
    documents,
    history,
    account,
    rangeId,
    documentCategory,
    accounts
}) {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setUnreadDocumentsPresent(false));
    }, [dispatch]);

    const handleScroll = useCallback(() => {
        if (isInProgress(requestStatus.status) || !nextPage) {
            return;
        }

        checkIfScrolledToBottom(() => fetchMoreCorrespondences(nextPage));
    }, [nextPage, requestStatus]);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        if (isFailure(requestStatus.status)) {
            window.scrollTo(0, 0);
        }
    }, [requestStatus.status]);

    const handleCategorySelect = event =>
        history.push(
            routes.client.documents({
                accountNum: account,
                rangeId: rangeId,
                documentCategory: event.target.value
            })
        );

    const handleAccountSelect = event =>
        history.push(
            routes.client.documents({
                accountNum: event.target.value,
                rangeId: rangeId,
                documentCategory: documentCategory
            })
        );

    const handleRangeSelected = (e, selection) =>
        history.push(
            routes.client.documents({
                accountNum: account,
                rangeId: selection.rangeId,
                documentCategory: documentCategory
            })
        );

    const filterDates = (doc, actualRange) => {
        const docDateMoment = moment(doc.sentDate);
        return docDateMoment.isBetween(
            actualRange.startDate,
            actualRange.endDate.endOf("day"),
            null,
            "[]"
        );
    };

    const updatedDocuments = useMemo(
        () => getDocumentsWithUpdatedCategories(documents),
        [documents]
    );
    const categoryOptions = getDocumentCategoryOptions(updatedDocuments);

    const getFilteredDocuments = (category, account, rangeId, read) => {
        const actualRange = convertIdToReal(rangeId);

        return updatedDocuments
            .filter(doc => filterAccounts(doc, account))
            .filter(doc => filterCategories(doc, category))
            .filter(doc => filterDates(doc, actualRange))
            .sort((a, b) => moment(b.sentDate) - moment(a.sentDate))
            .map(doc => (
                <Document
                    key={doc.documentId}
                    id={doc.documentId}
                    name={doc.displayName}
                    account={getAccountWrapperTitle(doc, accounts)}
                    sentDate={doc.sentDate}
                    link={doc._links.download.href}
                    read={read !== undefined ? read : doc.read}
                />
            ));
    };

    const refetch = () => {
        retryFetch(requestStatus.href || nextPage, setDocuments, "correspondences");
    };

    const { message, action } = getRefetchMessageAndAction(requestStatus, refetch);
    const documentsList = getFilteredDocuments(documentCategory, account, rangeId);

    return (
        <ContentHolder>
            <Message
                severity={ERROR}
                text={message}
                shown={!!message}
                errorCode={requestStatus.errorCode}
                marginTop="15px"
                action={action}
            />
            <H1>Documents</H1>
            <Container>
                <SelectGroup>
                    <SelectionBox
                        handleSelect={handleCategorySelect}
                        value={documentCategory}
                        options={categoryOptions}
                        aria-label="select document category"
                    />
                    <AccountSelection
                        handleSelect={handleAccountSelect}
                        value={account}
                        includeCombined
                    />
                    <DateContainer>
                        <SimplifiedSelectDateRange
                            dateChanged={handleRangeSelected}
                            value={rangeId}
                        />
                    </DateContainer>
                </SelectGroup>
            </Container>
            <Container>
                {documentsList}
                {isInProgress(requestStatus.status) && (
                    <Loading size={35} className={"spinner-big"} />
                )}
            </Container>
        </ContentHolder>
    );
}

const Container = styled.div`
    margin: auto;

    @media (min-width: ${props => props.theme.minWidth}) {
        padding-top: 20px;
        width: 100%;
        max-width: 860px;
        padding-right: 0.75em;
    }
`;

const DateContainer = styled.div`
    width: 100%;
    max-width: 300px;
`;

DocumentsList.defaultProps = {
    documents: [],
    requestStatus: {
        status: IN_PROGRESS
    }
};

DocumentsList.propTypes = {
    account: PropTypes.string,
    accounts: PropTypes.object,
    documentCategory: PropTypes.string,
    documents: PropTypes.array,
    totalDocuments: PropTypes.number,
    history: PropTypes.shape({
        push: PropTypes.func
    }),
    rangeId: PropTypes.string,
    requestStatus: requestObject,
    nextPage: PropTypes.string
};
